import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { AuditLogExplorer } from '@/components/users/AuditLogExplorer';
import { DEFAULT_AUDIT_FILTERS, type AuditFilters } from '@/features/users/auditLog';
import { useAuditActorSummary } from '@/features/users/hooks';
import { aalLabel, entityTypeLabel, summarizeUserAgent } from '@/features/users/auditLog';
import { formatDateTime, formatRelative } from '@/lib/formatters';

interface EmployeeActivityPanelProps {
  profileId: string;
  displayName: string;
}

/**
 * Everything one person has done in Atlas, pinned to their profile so a
 * reviewer can go straight from "who" to "what". Same data and same
 * super_admin gate as the Audit log page; the explorer is just locked to
 * this actor.
 */
export function EmployeeActivityPanel({ profileId, displayName }: EmployeeActivityPanelProps) {
  const [filters, setFilters] = useState<AuditFilters>(DEFAULT_AUDIT_FILTERS);
  const summary = useAuditActorSummary(profileId);

  return (
    <div className="space-y-4">
      <SummaryStrip summary={summary.data} loading={summary.isLoading} />
      <AuditLogExplorer
        lockedActorId={profileId}
        filters={filters}
        onFiltersChange={setFilters}
        title={`Activity by ${displayName}`}
        headerActions={
          <Button asChild variant="ghost" size="sm">
            <Link to={`/audit?actor=${encodeURIComponent(profileId)}`}>
              <ExternalLink className="h-3.5 w-3.5" />
              Open in Audit log
            </Link>
          </Button>
        }
      />
    </div>
  );
}

function SummaryStrip({
  summary,
  loading,
}: {
  summary: ReturnType<typeof useAuditActorSummary>['data'];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }
  if (!summary) return null;

  const lastAal = aalLabel(summary.last_auth_aal);
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <Stat label="Total events" value={summary.total.toLocaleString()}>
        <span>
          {summary.last_7d.toLocaleString()} in the last 7 days ·{' '}
          {summary.last_30d.toLocaleString()} in 30
        </span>
      </Stat>
      <Stat
        label="Last activity"
        value={summary.last_at ? formatRelative(summary.last_at) : '—'}
        title={summary.last_at ? formatDateTime(summary.last_at, { seconds: true }) : undefined}
      >
        {summary.first_at && <span>First seen {formatDateTime(summary.first_at)}</span>}
      </Stat>
      <Stat label="By action">
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="success-soft">{summary.inserts.toLocaleString()} created</Badge>
          <Badge variant="info-soft">{summary.updates.toLocaleString()} updated</Badge>
          <Badge variant="warning-soft">{summary.deletes.toLocaleString()} deleted</Badge>
          {summary.other > 0 && (
            <Badge variant="muted">{summary.other.toLocaleString()} other</Badge>
          )}
        </div>
      </Stat>
      <Stat
        label="Devices & locations"
        value={`${summary.distinct_ips.toLocaleString()} IP${summary.distinct_ips === 1 ? '' : 's'} · ${summary.distinct_sessions.toLocaleString()} session${summary.distinct_sessions === 1 ? '' : 's'}`}
      >
        <span className="font-mono">{summary.last_ip ?? '—'}</span>
        {summary.last_user_agent && <span> · {summarizeUserAgent(summary.last_user_agent)}</span>}
        {lastAal && <span> · {lastAal}</span>}
      </Stat>
      {summary.top_entity_types.length > 0 && (
        <Card className="sm:col-span-2 lg:col-span-4">
          <CardContent className="flex flex-wrap items-center gap-2 py-3 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Most touched</span>
            {summary.top_entity_types.map((t) => (
              <Badge key={t.entity_type} variant="outline">
                {entityTypeLabel(t.entity_type)} · {t.count.toLocaleString()}
              </Badge>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  title,
  children,
}: {
  label: string;
  value?: string;
  title?: string;
  children?: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="space-y-1 py-3">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        {value && (
          <p className="text-lg font-medium tracking-headline text-foreground" title={title}>
            {value}
          </p>
        )}
        {children && <div className="text-xs text-muted-foreground">{children}</div>}
      </CardContent>
    </Card>
  );
}
